import React from "react";
import { CheckGroup, SelectField } from "../../components/ui/UIComponents";

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 — Skills & Knowledge
// ─────────────────────────────────────────────────────────────────────────────

const TECH_SKILLS = [
  "C", "C++", "Java", "Python", "JavaScript",
  "HTML / CSS", "React / Angular / Vue",
  "Node.js / Express", "PHP / Laravel",
  "SQL / MySQL", "MongoDB",
  "AI / Machine Learning", "Data Science",
  "Android Development", "iOS / Swift",
  "Cloud (AWS / GCP / Azure)",
  "DevOps / Docker / Kubernetes",
  "Git / GitHub", "Linux / Shell",
  "Cybersecurity", "Networking",
  "Embedded Systems / Arduino",
  "AutoCAD / SolidWorks",
  "MS Office / Excel", "Other",
];

const SOFT_SKILLS = [
  "Communication", "Leadership",
  "Teamwork & Collaboration",
  "Public Speaking", "Problem Solving",
  "Critical Thinking", "Time Management",
  "Creativity & Innovation",
  "Adaptability", "Emotional Intelligence",
];

const LANGUAGES = [
  "English", "Hindi", "Kannada",
  "Tamil", "Telugu", "Malayalam",
  "Marathi", "Bengali", "Gujarati",
  "Punjabi", "Odia", "Assamese", "Other",
];

const EXPERIENCE_OPTIONS = [
  "No Experience (Fresher)",
  "Internship (< 6 months)",
  "Part-time / Project Work",
  "1 Year Work Experience",
  "2–3 Years Work Experience",
  "4+ Years Work Experience",
];

const CERTIFICATION_OPTIONS = [
  "None yet",
  "Google (IT Support, Data Analytics, etc.)",
  "AWS Cloud Practitioner / Solutions Architect",
  "Microsoft Azure / AZ-900",
  "Oracle Java / Python Certification",
  "NPTEL (IIT Courses)",
  "Coursera Specialization",
  "Udemy / LinkedIn Learning Certificate",
  "Competitive Programming (LeetCode, HackerRank)",
  "Other",
];

export default function Step4Skills({ form, setForm }) {
  const toggle = (key, val) =>
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val)
        ? f[key].filter(x => x !== val)
        : [...f[key], val],
    }));

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const techCount = form.techSkills?.length || 0;
  const softCount = form.softSkills?.length || 0;

  const profileStrength = Math.min(100, techCount * 8 + softCount * 5 + (form.languages?.length || 0) * 3);
  const strengthColor =
    profileStrength >= 75 ? "#10b981" :
    profileStrength >= 50 ? "#22d3ee" :
    profileStrength >= 25 ? "#f59e0b" : "#ef4444";
  const strengthLabel =
    profileStrength >= 75 ? "Strong Profile" :
    profileStrength >= 50 ? "Moderate Profile" :
    profileStrength >= 25 ? "Developing Profile" : "Beginner — Add More Skills";

  return (
    <div>
      {/* Skill strength meter */}
      <div style={{
        padding: "14px 18px",
        background: `${strengthColor}0a`,
        border: `1px solid ${strengthColor}30`,
        borderRadius: 10, marginBottom: 20,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, alignItems: "center" }}>
          <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 12, color: strengthColor, letterSpacing: 2 }}>
            ⚡ SKILL PROFILE STRENGTH
          </span>
          <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 22, fontWeight: 700, color: strengthColor }}>
            {profileStrength}%
          </span>
        </div>
        <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 4 }}>
          <div style={{
            height: "100%", borderRadius: 4,
            width: `${profileStrength}%`,
            background: `linear-gradient(90deg,${strengthColor},${strengthColor}88)`,
            transition: "width 0.5s ease",
          }} />
        </div>
        <p style={{ marginTop: 8, fontSize: 12, color: strengthColor, fontFamily: "Exo 2, sans-serif" }}>
          {strengthLabel} — {techCount} technical + {softCount} soft skills selected
        </p>
      </div>

      {/* Technical Skills */}
      <CheckGroup
        label="Technical Skills"
        items={TECH_SKILLS}
        selected={form.techSkills || []}
        onToggle={v => toggle("techSkills", v)}
      />

      {/* Soft Skills */}
      <CheckGroup
        label="Soft Skills"
        items={SOFT_SKILLS}
        selected={form.softSkills || []}
        onToggle={v => toggle("softSkills", v)}
      />

      {/* Languages */}
      <CheckGroup
        label="Languages Known"
        items={LANGUAGES}
        selected={form.languages || []}
        onToggle={v => toggle("languages", v)}
      />

      {/* Work Experience */}
      <SelectField
        label="Work / Internship Experience"
        value={form.experience}
        onChange={v => update("experience", v)}
        options={EXPERIENCE_OPTIONS}
      />

      {/* Certifications */}
      <SelectField
        label="Certifications Completed"
        value={form.certification}
        onChange={v => update("certification", v)}
        options={CERTIFICATION_OPTIONS}
      />

      {/* Competitive Coding */}
      <SelectField
        label="Competitive Coding Level"
        value={form.codingLevel}
        onChange={v => update("codingLevel", v)}
        options={[
          "Not started",
          "Beginner (< 50 problems solved)",
          "Intermediate (50–200 problems)",
          "Advanced (200–500 problems)",
          "Expert (500+ problems / Contests)",
        ]}
        hint="LeetCode, HackerRank, CodeChef, Codeforces"
      />
    </div>
  );
}